import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import {
  GridPaginationModel,
  GridSortModel,
  GridFilterModel,
  GridColumnVisibilityModel,
} from '@mui/x-data-grid'

export interface TableState {
  pagination: GridPaginationModel
  sort: GridSortModel
  filter: GridFilterModel
  columnVisibility: GridColumnVisibilityModel
  density: 'compact' | 'standard' | 'comfortable'
}

export interface UseTableStateManagerOptions {
  /** Unique identifier for this table's state */
  tableId: string
  /** Default pagination settings */
  defaultPagination?: GridPaginationModel
  /** Default sort settings */
  defaultSort?: GridSortModel
  /** Default filter settings */
  defaultFilter?: GridFilterModel
  /** Default column visibility */
  defaultColumnVisibility?: GridColumnVisibilityModel
  /** Default density */
  defaultDensity?: 'compact' | 'standard' | 'comfortable'
  /** Enable/disable persistence to localStorage */
  enablePersistence?: boolean
  /**
   * Ignore any saved sort on mount and start from `defaultSort` instead.
   *
   * For a table whose default order is part of how it is meant to be read, rather than a
   * preference each person tunes. The sort is still saved and can still be changed during
   * the session; only what gets *restored* changes, so pagination, filters and column
   * visibility keep persisting as before.
   */
  forceDefaultSortOnMount?: boolean
}

export interface UseTableStateManagerReturn {
  state: TableState
  setPagination: (model: GridPaginationModel) => void
  setSort: (model: GridSortModel) => void
  setFilter: (model: GridFilterModel) => void
  setColumnVisibility: (model: GridColumnVisibilityModel) => void
  setDensity: (density: 'compact' | 'standard' | 'comfortable') => void
  resetState: () => void
  saveState: () => void
  loadState: () => void
}

const STORAGE_PREFIX = 'treasury-table-state'

export const useTableStateManager = ({
  tableId,
  defaultPagination = { page: 0, pageSize: 25 },
  defaultSort = [],
  defaultFilter = { items: [] },
  defaultColumnVisibility = {},
  defaultDensity = 'compact',
  enablePersistence = true,
  forceDefaultSortOnMount = false,
}: UseTableStateManagerOptions): UseTableStateManagerReturn => {
  const storageKey = `${STORAGE_PREFIX}-${tableId}`

  const getDefaultState = useCallback((): TableState => ({
    pagination: defaultPagination,
    sort: defaultSort,
    filter: defaultFilter,
    columnVisibility: defaultColumnVisibility,
    density: defaultDensity,
  }), [
    defaultPagination,
    defaultSort,
    defaultFilter,
    defaultColumnVisibility,
    defaultDensity,
  ])

  const [state, setState] = useState<TableState>(() => getDefaultState())
  const hasLoadedInitialState = useRef(false)

  // Load state from localStorage on mount
  useEffect(() => {
    if (!enablePersistence || hasLoadedInitialState.current) return

    try {
      const savedState = localStorage.getItem(storageKey)
      if (savedState) {
        const parsedState = JSON.parse(savedState) as Partial<TableState>
        setState(prevState => ({
          ...prevState,
          ...parsedState,
          // Ensure we have all required properties
          pagination: parsedState.pagination || defaultPagination,
          // The saved sort is skipped entirely when the caller declares its order part of
          // the table's meaning rather than a per-person preference.
          sort: forceDefaultSortOnMount ? defaultSort : (parsedState.sort || defaultSort),
          filter: parsedState.filter || defaultFilter,
          columnVisibility: parsedState.columnVisibility || defaultColumnVisibility,
          density: parsedState.density || defaultDensity,
        }))
      }
      hasLoadedInitialState.current = true
    } catch (error) {
      console.warn(`Failed to load table state for ${tableId}:`, error)
      setState(getDefaultState())
      hasLoadedInitialState.current = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey, enablePersistence, forceDefaultSortOnMount])

  // Save state to localStorage
  const saveState = useCallback(() => {
    if (!enablePersistence) return

    try {
      localStorage.setItem(storageKey, JSON.stringify(state))
    } catch (error) {
      console.warn(`Failed to save table state for ${tableId}:`, error)
    }
  }, [state, storageKey, tableId, enablePersistence])

  // Auto-save state whenever it changes (debounced to prevent excessive saves)
  useEffect(() => {
    // Don't auto-save during initial load
    if (!hasLoadedInitialState.current) return

    const timeoutId = setTimeout(() => {
      if (enablePersistence) {
        try {
          localStorage.setItem(storageKey, JSON.stringify(state))
        } catch (error) {
          console.warn(`Failed to save table state for ${tableId}:`, error)
        }
      }
    }, 300) // Debounce save by 300ms
    
    return () => clearTimeout(timeoutId)
  }, [state, storageKey, tableId, enablePersistence])

  // Load state from localStorage
  const loadState = useCallback(() => {
    if (!enablePersistence) return

    try {
      const savedState = localStorage.getItem(storageKey)
      if (savedState) {
        const parsedState = JSON.parse(savedState) as Partial<TableState>
        setState(prevState => ({ ...prevState, ...parsedState }))
      }
    } catch (error) {
      console.warn(`Failed to load table state for ${tableId}:`, error)
    }
  }, [storageKey, tableId, enablePersistence])

  // Reset state to defaults
  const resetState = useCallback(() => {
    setState(getDefaultState())
    hasLoadedInitialState.current = false
    if (enablePersistence) {
      try {
        localStorage.removeItem(storageKey)
      } catch (error) {
        console.warn(`Failed to clear table state for ${tableId}:`, error)
      }
    }
  }, [getDefaultState, storageKey, tableId, enablePersistence])

  // State setters that trigger auto-save
  const setPagination = useCallback((model: GridPaginationModel) => {
    setState(prev => ({ ...prev, pagination: model }))
  }, [])

  const setSort = useCallback((model: GridSortModel) => {
    setState(prev => ({ ...prev, sort: model }))
  }, [])

  const setFilter = useCallback((model: GridFilterModel) => {
    setState(prev => ({ ...prev, filter: model }))
  }, [])

  const setColumnVisibility = useCallback((model: GridColumnVisibilityModel) => {
    setState(prev => ({ ...prev, columnVisibility: model }))
  }, [])


  const setDensity = useCallback((density: 'compact' | 'standard' | 'comfortable') => {
    setState(prev => ({ ...prev, density }))
  }, [])

  // Memoize the return object to prevent unnecessary re-renders
  return useMemo(() => ({
    state,
    setPagination,
    setSort,
    setFilter,
    setColumnVisibility,
    setDensity,
    resetState,
    saveState,
    loadState,
  }), [
    state,
    setPagination,
    setSort,
    setFilter,
    setColumnVisibility,
    setDensity,
    resetState,
    saveState,
    loadState,
  ])
}

export default useTableStateManager